import { StylesProps } from 'src/library/types';
import { AssignedDriverStyles } from './AssignedDrivers.styles';
import { AddDriverStyle } from './AddNewDriverModal.styles';

type Nullable<T> = T | null;
export interface IBucketList {
  pretty_name: string;
  column_id: string;
  is_sortable: boolean;
  sort_key: string;
}

export interface AssignedDriversRecord {
  worker_code: string,
  name: string,
  driving_license_number: number,
  created_at: number,
  is_active: boolean,
}
export interface FieldItem {
  key: string;
  label: string;
  is_optional: boolean;
  is_applicable_for_self_onboarding: boolean;
  is_proof_mandatory?: boolean;
}
export interface ImageUploaderProps {
  fieldItem: FieldItem;
}

export interface IAssignedDrivers {
  loader: boolean;
  is_next_present: boolean;
  last_row_id_prev: number;
  last_row_id_next: number;
  last_sorted_column_value_prev: number;
  last_sorted_column_value_next: number;
  last_sort_by_key: string;
  page_data: AssignedDriversRecord[];
  count: {
    [key: string]: number;
  };
  filters: {
    timeFilters: {
      columnName: string;
      fromDateString?: string;
      toDateString?: string;
    };
  };
  pagination: {
    resultPerPage: number;
    nextOrPrev?: string;
    pageNumber: number;
  };
}
interface WorkerMasterConfig {
  allow_driver_creation: boolean;
  allow_driver_update: boolean;
  fields_list: Field[];
}
interface Field {
  key: string;
  label?: string;
  regex?: string;
  type?: string;
  is_optional?: boolean;
  sanitize?: boolean;
  allow_multiple?: boolean;
  value_list?: string[];
  is_applicable_for_self_onboarding?: boolean;
  required_documents?: Document[];
  autofills?: string[];
}
interface Document {
  key: string;
  label?: string;
  is_optional: boolean;
  regex?: string;
  type?: string;
  autofills?: string[];
  is_applicable_for_self_onboarding?: boolean;
}
interface LanguageConfig {
  allowed_language_list: string[];
  default_language: string;
}
interface TripsPropertiesToInput {
  key: string;
  label: string;
}
interface VendorConfig {
  trips_properties_to_input: TripsPropertiesToInput[];
  language_config: LanguageConfig;
  worker_master_config: WorkerMasterConfig;
}
export interface AssignedDriversProps extends StylesProps<ReturnType<typeof AssignedDriverStyles>> {
  assignedDrivers: IAssignedDrivers;
  getAssignedDrivers: (data) => void;
  setPagination: (data) => void;
  columnList: IBucketList[];
  t: any;
  vendorConfig: VendorConfig;
}

export interface DriverData {
    created_at: string;
    driving_licence_expiry_date?: string;
    driving_license_number?: string;
    id: string;
    is_active?: boolean;
    name?: string;
    phone?: string;
    worker_code?: string;
}
export interface ValidationStrategy {
  strategy_type: 'input_filter' | 'regex' | 'external';
  reference_key?: string;
  regex?: string;
  error?: string;
  executor?: string;
  [key: string]: ValidationStrategy | string | undefined;
}

export interface ValidationConfig {
  [fieldName: string]: ValidationStrategy;
}

export interface AddNewDriverModalProps extends StylesProps<ReturnType<typeof AddDriverStyle>> {
  handleModalClose: () => void;
  isVisible: boolean;
  action: string;
  driverData?: Nullable<DriverData>;
  fields_list: Field[];
  allowed_jfl_worker_types: string[];
  useAddressHierarchyForLocationFields: boolean;
  workerValidationStrategy: ValidationConfig;
  autoGenerateWorkerDetails: boolean;
  reloadView: () => void;
}
export interface FetchRiderDetails {
    username: string;
    name: string;
    phone: string | null;
    driving_licence_number: string;
    worker_code: string;
    hub_code: string;
    worker_type: string | null;
    worker_role: string | null;
    worker_category_id: number | null;
    destination_vpa: string | null;
    rule_identifier: string | null;
    is_eligible_for_allocation: boolean;
    worker_ops_status: string;
    gender: string | null;
    upstream_worker_code: string;
    allowed_task_types: string[] | null;
    national_id_type: string | null;
    national_id_number: string | null;
    driving_licence_validity: string; // ISO date string
    license_category_list: string[];
    vehicle_id: number | null;
    vehicle_registration_number: string | null;
    vehicle_make_name: string | null;
    vehicle_category_id: number | null;
    vehicle_category_label: string | null;
    worker_vendor_name: string;
    active_for_pickup: boolean;
    active_for_drs_slot: boolean;
    active_for_hub_ops: boolean;
    preferred_vehicle_type: string | null;
    second_preferred_vehicle_type: string | null;
    extra_details: ExtraDetails;
    address_line_1: string | null;
    address_line_2: string | null;
    city: string;
    state: string;
    pincode: string | null;
    country: string;
    date_of_joining: string | null;
    shifts: string | null;
    blood_group: string | null;
    constraint_tags: string[];
    identification_documents: IdentificationDocument[];
    available_for_trip_action: boolean;
  }

  interface ExtraDetails {
    blood_group: string | null;
    constraint_tags: string[];
    license_category_list: string[];
    driving_licence_number: string;
    driving_licence_validity: string;
    identification_documents: IdentificationDocument[];
    available_for_trip_action: boolean;
  }

  interface IdentificationDocument {
    type: string;
    image: string;
    number: string;
  }

  export const identificationDocumentsColumns = [
    {
      pretty_name: 'Document Type',
      column_id: 'type',
    },
    {
      pretty_name: 'Document Number',
      column_id: 'number',
    },
    {
      pretty_name: 'Proof',
      column_id: 'image',
      is_signed_url: true,
    },
  ];
